/* eslint-disable */
/* eslint-disable no-unused-vars */
import { Card, Collapse, FormInstance, Typography, Select, Space, Spin, Button } from 'antd';
import { useState, useEffect } from 'react';
import { InputInterface } from '../utils/forms/FormInterface';
import ReviewPartnerOrderMember from './ReviewPartnerOrderMember';
import UserDetails from './UserDetails';
import PaymentDetails from './PaymentDetails';
import { getRole } from '../service/CookieManager';
import { PARTNER_ORDERS } from '../constants/EntityName';
import { partnerMemberStatusOptions } from '../constants/AppConstant';
import callApi from '../Api/config';
import { toastSuccess, toastError } from '../service/Toast';
import {
  ClipboardList,
  FileStack,
  LineChart,
  Clipboard,
  CircleCheckBig,
  Building2,
  ShoppingCart,
  Users,
  User,
  Mail,
  ShieldCheck,
  Hash,
  Calendar,
  Users2,
  BriefcaseBusiness,
  MapPin,
  CreditCard,
  Package,
  CircleDollarSign,
  Check,
  ScanLine,
} from 'lucide-react';

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface ReviewPartnerOrderProps {
  formInput: InputInterface;
  index: number;
  form: FormInstance;
  isCreateMode?: boolean;
  additionalData?: any;
  formDetails?: any;
  onPrimaryTabChange?: (tabId: string) => void;
}

const ReviewPartnerOrder = (props: ReviewPartnerOrderProps) => {
  const { formDetails, onPrimaryTabChange } = props;
  const [members, setMembers] = useState<any[]>(formDetails?.members || []);
  const [partnerDetails, setPartnerDetails] = useState<any>(null);
  const [paymentDetails, setPaymentDetails] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [activePrimaryTab, setActivePrimaryTab] = useState<string>('partner-info');
  const [hasAutoSelectedMemberTab, setHasAutoSelectedMemberTab] = useState<boolean>(false);
  const [activeSecondaryTabs, setActiveSecondaryTabs] = useState<{ [memberId: string]: string }>(
    {}
  );
  const [orderIdCopied, setOrderIdCopied] = useState<boolean>(false);

  // Per-member Application Status state (moved from Documents subtab bottom)
  const [memberStatusMap, setMemberStatusMap] = useState<{ [memberId: string]: string }>({});
  const [memberStatusUpdating, setMemberStatusUpdating] = useState<{ [memberId: string]: boolean }>(
    {}
  );

  // Helper to derive default status for a member (same logic as in ReviewPartnerOrderMember)
  const getDefaultMemberStatus = (member: any): string => {
    const status = member?.status;
    if (status) {
      const found = partnerMemberStatusOptions.find(
        (o: any) => o.value === status || o.value?.toLowerCase() === status?.toLowerCase()
      );
      return found ? found.value : status;
    }
    const formDataEntry = member?.formEntries?.[0];
    const steps = formDataEntry?.formData?.steps;
    if (!steps) return 'Order Pending';
    return 'Document Pending';
  };

  // Initialize status map when members load
  useEffect(() => {
    if (Array.isArray(members) && members.length > 0) {
      const nextMap: { [memberId: string]: string } = {};
      members.forEach(m => {
        if (m?.id) {
          nextMap[m.id] = memberStatusMap[m.id] ?? getDefaultMemberStatus(m);
        }
      });
      setMemberStatusMap(prev => ({ ...prev, ...nextMap }));
    }
  }, [members]);

  // PATCH /partner-portal/members/:id/status  (exact same API as ReviewPartnerOrderMember)
  const handleMemberStatusUpdateFromHeader = async (member: any) => {
    const memberId = member?.id;
    const status = memberStatusMap[memberId];
    if (!memberId || !status) return;
    try {
      setMemberStatusUpdating(prev => ({ ...prev, [memberId]: true }));
      const res = await callApi(`/partner-portal/members/${memberId}/status`, 'PATCH', { status });
      if (res?.data?.statusCode === 200 || res?.status) {
        toastSuccess('Member status updated successfully');
        // Update local members array so primary tab badges refresh
        setMembers((prev: any[]) =>
          prev.map((m: any) => (m.id === memberId ? { ...m, status } : m))
        );
        // Also bubble up via the existing callback (same behavior as before)
        handleMemberStatusUpdated(memberId, status);
      } else {
        toastError('Failed to update member status');
      }
    } catch (error: any) {
      console.error('Error updating member status:', error);
      toastError(error?.response?.data?.message || 'Failed to update member status');
    } finally {
      setMemberStatusUpdating(prev => ({ ...prev, [memberId]: false }));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!formDetails) {
        setLoading(false);
        return;
      }

      try {
        // Load partner details
        if (formDetails.partner) {
          setPartnerDetails({
            id: formDetails.partner?.id,
            name: formDetails.partner?.partnerName || formDetails.partner?.name || 'Partner',
            email: formDetails.partner?.email || '--',
            phone: formDetails.partner?.mobileNumber || formDetails.partner?.phone || '--',
          });
        }

        // Derive pricing based on partner package customPrice and member count
        const memberCount = (formDetails.members && formDetails.members.length) || 0;
        const customPrice = formDetails?.partnerPackage?.customPrice;
        const perMemberPrice =
          customPrice !== undefined && customPrice !== null ? Number(customPrice) : undefined;
        const totalPrice = perMemberPrice && memberCount ? perMemberPrice * memberCount : undefined;

        // Set payment details (similar structure to Documitra orders)
        const paymentObj = {
          id: formDetails?.id,
          orderId: formDetails?.groupCode || formDetails?.id,
          trackingId: formDetails?.trackingId || '--',
          packageName: formDetails?.partnerPackage?.package?.name || '--',
          serviceName: formDetails?.partnerPackage?.name || '--',
          pricingPlan: {
            title: formDetails?.partnerPackage?.name || '--',
            // Price per member (from customPrice or package default)
            discountedPricePerMember: perMemberPrice,
            discountedPrice: totalPrice ? String(totalPrice) : undefined,
          },
          // Expose member count so PaymentDetails can show group total
          groupMemberCount: memberCount,
          perMemberPrice: perMemberPrice,
          totalPrice: totalPrice,
          paymentDate: formDetails?.createdAt
            ? new Date(formDetails.createdAt).toLocaleDateString()
            : '--',
          paymentDetails: {
            paymentStatus: formDetails?.paymentStatus || formDetails?.status || '--',
            provider: 'Partner Wallet',
            // Calculated total amount for the group (read-only display)
            amountPaid: totalPrice ? String(totalPrice) : '--',
          },
          photoEnhancement: {},
          expedite: {},
          apostilled: {},
        };
        setPaymentDetails(paymentObj);

        // Load members if not already loaded
        if (formDetails.members && formDetails.members.length > 0) {
          setMembers(formDetails.members);
        } else {
          // Try to fetch members from API if not in formDetails
          try {
            const response = await callApi(`${PARTNER_ORDERS}/${formDetails.id}`, 'GET');
            if (response?.data?.data?.members) {
              setMembers(response.data.data.members);
            }
          } catch (error) {
            console.error('Error loading members:', error);
          }
        }
      } catch (error) {
        console.error('Error loading partner order data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [formDetails]);

  useEffect(() => {
    if (
      !hasAutoSelectedMemberTab &&
      Array.isArray(members) &&
      members.length > 0 &&
      members[0]?.id
    ) {
      const firstMemberId = members[0].id;
      setActivePrimaryTab(`member-${firstMemberId}`);
      setActiveSecondaryTabs(prevSecondary => {
        if (prevSecondary[firstMemberId]) return prevSecondary;
        return { ...prevSecondary, [firstMemberId]: `form-details-${firstMemberId}` };
      });
      setHasAutoSelectedMemberTab(true);
    }
  }, [members, hasAutoSelectedMemberTab]);

  useEffect(() => {
    onPrimaryTabChange?.(activePrimaryTab);
  }, [activePrimaryTab, onPrimaryTabChange]);

  const currentUserRole: any = getRole();

  const handleMemberStatusUpdate = async (memberId: string, status: string) => {
    try {
      const res = await callApi(`/partner-portal/members/${memberId}/status`, 'PATCH', { status });
      if (res?.data?.statusCode === 200 || res?.status) {
        toastSuccess('Member status updated successfully');
        // Update only the specific member - each member has independent status
        const updatedMembers = members.map((m: any) => (m.id === memberId ? { ...m, status } : m));
        setMembers(updatedMembers);
      } else {
        toastError('Failed to update member status');
      }
    } catch (error: any) {
      console.error('Error updating member status:', error);
      toastError(error?.response?.data?.message || 'Failed to update member status');
    }
  };

  /** Callback when child updates member status - sync parent state so only that member is updated */
  const handleMemberStatusUpdated = (memberId: string, status: string) => {
    setMembers((prev: any[]) => prev.map((m: any) => (m.id === memberId ? { ...m, status } : m)));
  };

  const handleGroupStatusUpdate = async (status: string) => {
    try {
      const res = await callApi(`/partner-portal/groups/${formDetails.id}/status`, 'PATCH', {
        status,
      });
      if (res?.data?.statusCode === 200 || res?.status) {
        toastSuccess('Group status updated successfully');
        // Reload data to reflect changes
        const response = await callApi(`${PARTNER_ORDERS}/${formDetails.id}`, 'GET');
        if (response?.data?.data) {
          // Update formDetails with new status
          // This will trigger useEffect to reload
        }
      } else {
        toastError('Failed to update group status');
      }
    } catch (error: any) {
      console.error('Error updating group status:', error);
      toastError(error?.response?.data?.message || 'Failed to update group status');
    }
  };

  const PrintField = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#53657f',
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '15px',
          fontWeight: 600,
          color: '#0f172a',
          lineHeight: 1.35,
        }}
      >
        {value || '—'}
      </span>
    </div>
  );

  const NAVY = 'black';

  const orderCardStyle: React.CSSProperties = {
    width: '100%',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    padding: '18px 20px',
    marginBottom: '18px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: NAVY,
    marginBottom: '14px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e2e8f0',
    lineHeight: 1.2,
  };

  const fieldGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    columnGap: '28px',
    rowGap: '14px',
  };

  const PaymentStatusBadge = () => {
    const raw: string = formDetails?.paymentStatus || '';
    const norm = raw.trim().toLowerCase();
    let label = raw;
    let style: { bg: string; color: string; ring: string } = {
      bg: '#f1f5f9',
      color: '#475569',
      ring: 'rgba(71,85,105,0.2)',
    };
    if (
      norm === 'payment-done' ||
      norm === 'paid' ||
      norm === 'succeeded' ||
      norm === 'payment done'
    ) {
      label = 'Payment Done';
      style = { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' };
    } else if (norm === 'payment pending' || norm === 'pending') {
      label = 'Payment Pending';
      style = { bg: '#fffbeb', color: '#b45309', ring: 'rgba(180,83,9,0.2)' };
    } else if (norm === 'payment successful') {
      style = { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' };
    } else if (norm === 'refund issued') {
      style = { bg: '#fdf2f8', color: '#9d174d', ring: 'rgba(157,23,77,0.2)' };
    } else if (!raw) {
      label = 'Pending';
      style = { bg: '#fffbeb', color: '#b45309', ring: 'rgba(180,83,9,0.2)' };
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          width: 'fit-content',
          alignItems: 'center',
          borderRadius: '999px',
          padding: '3px 12px',
          fontSize: '13px',
          fontWeight: 600,
          background: style.bg,
          color: style.color,
          boxShadow: `inset 0 0 0 1px ${style.ring}`,
          lineHeight: 1.5,
        }}
      >
        {label}
      </span>
    );
  };

  const renderPartnerInfo = () => {
    const partner = formDetails?.partner;
    const applyingFrom =
      formDetails?.applyingFromCountry ||
      formDetails?.applyingFrom ||
      formDetails?.group?.applyingFromCountry ||
      '--';

    const NAVY = '#004195';

    const partnerField = (Icon: any, label: string, value: React.ReactNode) => (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div
          style={{
            marginTop: '2px',
            display: 'flex',
            height: '32px',
            width: '32px',
            flex: '0 0 32px',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            background: 'rgba(0, 48, 135, 0.06)',
            color: NAVY,
          }}
        >
          <Icon size={15} strokeWidth={2} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span
            style={{
              fontSize: '15px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              color: '#94a3b8',
            }}
          >
            {label}
          </span>
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            {value || '—'}
          </span>
        </div>
      </div>
    );

    const partnerCardStyle: React.CSSProperties = {
      width: '100%',
      borderRadius: '14px',
      border: '1px solid #e2e8f0',
      background: '#fff',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    };

    const partnerCardHeaderStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '14px',
      paddingBottom: '14px',
      borderBottom: '1px solid #f1f5f9',
    };

    const partnerCardHeaderLabelStyle: React.CSSProperties = {
      fontSize: '16px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      color: '#64748b',
    };

    const partnerFieldGridStyle: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      columnGap: '14px',
      rowGap: '14px',
    };

    return (
      <div key={formDetails?.id} style={{ marginBottom: '20px' }}>
        <div
          style={{
            fontSize: '19px',
            fontWeight: 600,
            color: '#1e293b',
            marginBottom: '14px',
          }}
        >
          Agent Basic Details
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingTop: '4px',
          }}
        >
          <div style={partnerCardStyle}>
            <div style={partnerCardHeaderStyle}>
              <Building2 size={15} color={NAVY} />
              <span style={partnerCardHeaderLabelStyle}>Partner Agent</span>
            </div>
            <div style={partnerFieldGridStyle}>
              {partnerField(User, 'Agent Name', partner?.partnerName || partner?.name || '--')}
              {partnerField(Mail, 'Agent Email', partner?.email || '--')}
              {partnerField(ShieldCheck, 'Agent Status', partner?.status || 'Active')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderInfo = () => {
    const planType =
      formDetails?.partnerPackage?.readyToShip === true ||
      formDetails?.partnerPackage?.readyToShip === 'true'
        ? 'Ready to Ship'
        : 'Online Consultation';
    const applyingFrom =
      formDetails?.applyingFromCountry ||
      formDetails?.applyingFrom ||
      formDetails?.group?.applyingFromCountry ||
      '--';

    const orderField = (label: string, value: React.ReactNode) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span
          style={{
            fontSize: '15px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#53657f',
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{value || '—'}</span>
      </div>
    );

    const orderCardStyle: React.CSSProperties = {
      width: '100%',
      borderRadius: '14px',
      border: '1px solid #e2e8f0',
      background: '#fff',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    };

    const orderSectionHeaderStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '14px',
      paddingBottom: '14px',
      borderBottom: '1px solid #f1f5f9',
    };

    const orderSectionHeaderLabelStyle: React.CSSProperties = {
      fontSize: '16px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      color: 'black',
    };

    const orderFieldGridStyle: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      columnGap: '14px',
      rowGap: '14px',
    };

    const orderSectionDividerStyle: React.CSSProperties = {
      marginTop: '24px',
      paddingTop: '16px',
      borderTop: '1px dashed #e2e8f0',
    };

    return (
      <>
        <div style={orderCardStyle}>
          {/* Order & Group Information  */}
          <div>
            <div style={orderSectionHeaderStyle}>
              <span style={orderSectionHeaderLabelStyle}>Order & Group Information</span>
            </div>
            <div style={orderFieldGridStyle}>
              {orderField('Order ID', formDetails?.groupCode || '--')}
              {orderField(
                'Order Date',
                formDetails?.createdAt ? new Date(formDetails.createdAt).toLocaleDateString() : '--'
              )}
              {orderField('Group Name', formDetails?.groupName || '--')}
            </div>
          </div>

          {/* Pricing Details (dashed divider) */}
          <div style={orderSectionDividerStyle}>
            {/* <div style={orderSectionHeaderStyle}>
              <span style={orderSectionHeaderLabelStyle}>Pricing Details</span>
            </div> */}
            <div style={orderFieldGridStyle}>
              {orderField('Selected Plan', planType)}
              {orderField(
                'Price Per Member',
                paymentDetails?.perMemberPrice ? `$${paymentDetails.perMemberPrice}` : '--'
              )}
              {orderField(
                'Total Price',
                paymentDetails?.totalPrice ? `$${paymentDetails.totalPrice}` : '--'
              )}
            </div>
          </div>

          {/* Service & Payment (dashed divider) */}
          <div style={orderSectionDividerStyle}>
            {/* <div style={orderSectionHeaderStyle}>
              <span style={orderSectionHeaderLabelStyle}>Service & Payment</span>
            </div> */}
            <div style={orderFieldGridStyle}>
              {orderField('Applying From', applyingFrom)}
              {orderField(
                'Selected Service',
                formDetails?.partnerPackage?.name ||
                  formDetails?.partnerPackage?.package?.name ||
                  '--'
              )}
              <div>
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    color: '#53657f',
                    display: 'block',
                    marginBottom: '2px',
                  }}
                >
                  Payment Status
                </span>
                <PaymentStatusBadge />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Member-specific tracking details (called from secondary tab inside each member)
  const renderMemberTrackingDetails = (member: any) => {
    return (
      <div
        style={{
          width: '100%',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          background: '#fff',
          padding: '18px 20px',
          marginBottom: '18px',
        }}
      >
        <div
          style={{
            fontSize: '16px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'black',
            marginBottom: '14px',
            paddingBottom: '10px',
            borderBottom: '1px solid #e2e8f0',
            lineHeight: 1.2,
          }}
        >
          Additional Application Details
        </div>
        <ReviewPartnerOrderMember
          formInput={props.formInput}
          index={members.indexOf(member)}
          form={props.form}
          formDetails={{ ...member, group: formDetails }}
          showOnlyAdditionalDetails={true}
          onMemberStatusUpdated={handleMemberStatusUpdated}
        />
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const orderIdValue = formDetails?.groupCode || formDetails?.id || '--';

  return (
    <>
      {/* ========================================================= */}
      {/* PRIMARY TOP CARD: Order ID (left) + Primary Tabs (right) */}
      {/* ========================================================= */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          marginBottom: '20px',
        }}
      >
        {/* Order ID Label */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              position: 'absolute',
              backgroundColor: 'aliceblue',
              border: '1px solid rgb(226, 232, 240)',
              padding: '0px 8px',
              borderRadius: '4px',
              top: '-30px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Text
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#0f172a',
                  margin: 0,
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                {orderIdValue}
              </Text>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  title={orderIdCopied ? 'Copied!' : 'Copy Order ID'}
                  onClick={() => {
                    if (orderIdValue && navigator?.clipboard) {
                      navigator.clipboard
                        .writeText(String(orderIdValue))
                        .then(() => {
                          setOrderIdCopied(true);
                          window.setTimeout(() => setOrderIdCopied(false), 1600);
                        })
                        .catch(() => {});
                    }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    border: 'none',
                    background: orderIdCopied ? '#e6f4ea' : 'transparent',
                    cursor: 'pointer',
                    color: orderIdCopied ? '#1e8e3e' : '#0b63d1',
                    padding: 0,
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    if (!orderIdCopied) {
                      e.currentTarget.style.backgroundColor = '#eef2ff';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!orderIdCopied) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {orderIdCopied ? (
                    <CircleCheckBig size={18} strokeWidth={2.25} />
                  ) : (
                    <Clipboard size={18} strokeWidth={2} />
                  )}
                </button>
                {orderIdCopied && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                      backgroundColor: '#0f172a',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      boxShadow: '0 4px 10px rgba(15, 23, 42, 0.2)',
                      zIndex: 50,
                      pointerEvents: 'none',
                      letterSpacing: '0.01em',
                    }}
                  >
                    Copied!
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Primary Tabs Navigation (pill style) */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            overflowX: 'auto',
            padding: '4px',
            backgroundColor: '#eef1f5',
            border: '0',
            borderRadius: '10px',
            scrollbarWidth: 'none',
          }}
        >
          {/* Partner Information Tab */}
          <button
            type="button"
            onClick={() => setActivePrimaryTab('partner-info')}
            style={{
              flex: '0 0 auto',
              padding: '7px 16px',
              border: '0',
              borderRadius: '7px',
              background: activePrimaryTab === 'partner-info' ? '#ffffff' : 'transparent',
              color: activePrimaryTab === 'partner-info' ? '#004195' : '#60758d',
              fontWeight: 600,
              fontSize: 'medium',
              letterSpacing: '0.01em',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow:
                activePrimaryTab === 'partner-info'
                  ? '0 1px 2px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)'
                  : 'none',
              transition: 'color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            <Building2 size={15} strokeWidth={2.25} />
            Agent Information
          </button>

          {/* Order Information Tab */}
          <button
            type="button"
            onClick={() => setActivePrimaryTab('order-info')}
            style={{
              flex: '0 0 auto',
              padding: '7px 16px',
              border: '0',
              borderRadius: '7px',
              background: activePrimaryTab === 'order-info' ? '#ffffff' : 'transparent',
              color: activePrimaryTab === 'order-info' ? '#004195' : '#60758d',
              fontWeight: 600,
              fontSize: 'medium',
              letterSpacing: '0.01em',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow:
                activePrimaryTab === 'order-info'
                  ? '0 1px 2px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)'
                  : 'none',
              transition: 'color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            <ShoppingCart size={15} strokeWidth={2.25} />
            Order Information
          </button>

          {/* Group Members Tabs (one tab per member) - each member gets own primary tab */}
          {currentUserRole !== 'Accountant' &&
            members?.map((member: any) => {
              const tabId = `member-${member?.id}`;
              const isActive = activePrimaryTab === tabId;
              const memberStatus = member?.status || '--';
              const normalizedStatus = String(memberStatus || '')
                .trim()
                .toLowerCase();
              let badgeBg = '#eef2ff';
              let badgeBorder = '#c7d2fe';
              let badgeColor = '#3730a3';
              if (normalizedStatus.includes('complete') || normalizedStatus.includes('approved')) {
                badgeBg = '#ecfdf5';
                badgeBorder = '#a7f3d0';
                badgeColor = '#047857';
              } else if (
                normalizedStatus.includes('reject') ||
                normalizedStatus.includes('decline') ||
                normalizedStatus.includes('cancel')
              ) {
                badgeBg = '#fef2f2';
                badgeBorder = '#fecaca';
                badgeColor = '#b91c1c';
              } else if (
                normalizedStatus.includes('pending') ||
                normalizedStatus.includes('review') ||
                normalizedStatus.includes('wait') ||
                normalizedStatus.includes('in progress') ||
                normalizedStatus.includes('processing')
              ) {
                badgeBg = '#fff7ed';
                badgeBorder = '#fed7aa';
                badgeColor = '#c2410c';
              }
              return (
                <button
                  key={member?.id}
                  type="button"
                  onClick={() => setActivePrimaryTab(tabId)}
                  style={{
                    flex: '0 0 auto',
                    padding: '7px 12px 7px 16px',
                    border: '0',
                    borderRadius: '7px',
                    background: isActive ? '#ffffff' : 'transparent',
                    color: isActive ? '#004195' : '#60758d',
                    fontWeight: 600,
                    fontSize: 'medium',
                    letterSpacing: '0.01em',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: isActive
                      ? '0 1px 2px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)'
                      : 'none',
                    transition:
                      'color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  <Users size={15} strokeWidth={2.25} />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        maxWidth: '160px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      {member?.name}
                    </span>
                    {memberStatus && memberStatus !== '--' && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 10px',
                          borderRadius: '999px',
                          border: `1px solid ${badgeBorder}`,
                          backgroundColor: badgeBg,
                          color: badgeColor,
                          fontSize: '11.5px',
                          fontWeight: 600,
                          lineHeight: 1.3,
                          letterSpacing: '0.01em',
                          maxWidth: '170px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                          flexShrink: 0,
                        }}
                      >
                        {memberStatus}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
        </div>
      </div>

      {/* ========================================================= */}
      {/* PRIMARY CONTENT PANEL */}
      {/* ========================================================= */}
      <div>
        {/* Partner Information Panel */}
        {activePrimaryTab === 'partner-info' && renderPartnerInfo()}

        {/* Order Information Panel */}
        {activePrimaryTab === 'order-info' && renderOrderInfo()}

        {/* Group Member Panels */}
        {currentUserRole !== 'Accountant' &&
          members?.map((member: any) => {
            const tabId = `member-${member?.id}`;
            if (activePrimaryTab !== tabId) return null;
            const secondaryTabId = activeSecondaryTabs[member?.id] || `form-details-${member?.id}`;

            return (
              <div
                key={member?.id}
                style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}
              >
                <style>{`
                  .sidebar-menu-btn:hover {
                    background-color: #f8fafc !important;
                    border-color: #cbd5e1 !important;

                    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04) !important;
                  }
                  .sidebar-menu-btn:active {

                    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06) !important;
                  }
                `}</style>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    width: '240px',
                    flexShrink: 0,
                    position: 'sticky',
                    top: '16px',
                    padding: '12px 10px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    zIndex: 5,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setActiveSecondaryTabs(prev => ({
                          ...prev,
                          [member?.id]: `form-details-${member?.id}`,
                        }))
                      }
                      className="sidebar-menu-btn"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border:
                          secondaryTabId === `form-details-${member?.id}`
                            ? '1px solid #bcd7ff'
                            : '1px solid #e2e8f0',
                        background:
                          secondaryTabId === `form-details-${member?.id}` ? '#eff6ff' : '#ffffff',
                        color:
                          secondaryTabId === `form-details-${member?.id}` ? '#075dc5' : '#334155',
                        fontWeight: secondaryTabId === `form-details-${member?.id}` ? 600 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        borderRadius: '8px',
                        textAlign: 'left',
                        boxShadow:
                          secondaryTabId === `form-details-${member?.id}`
                            ? '0 1px 3px rgba(7, 93, 197, 0.12), inset 0 1px 0 rgba(255,255,255,0.6)'
                            : '0 1px 2px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          backgroundColor:
                            secondaryTabId === `form-details-${member?.id}` ? '#dbeafe' : '#f1f5f9',
                          color:
                            secondaryTabId === `form-details-${member?.id}` ? '#075dc5' : '#64748b',
                        }}
                      >
                        <ClipboardList size={15} strokeWidth={2.25} />
                      </div>
                      Form Details
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setActiveSecondaryTabs(prev => ({
                          ...prev,
                          [member?.id]: `documents-${member?.id}`,
                        }))
                      }
                      className="sidebar-menu-btn"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border:
                          secondaryTabId === `documents-${member?.id}`
                            ? '1px solid #bcd7ff'
                            : '1px solid #e2e8f0',
                        background:
                          secondaryTabId === `documents-${member?.id}` ? '#eff6ff' : '#ffffff',
                        color: secondaryTabId === `documents-${member?.id}` ? '#075dc5' : '#334155',
                        fontWeight: secondaryTabId === `documents-${member?.id}` ? 600 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        borderRadius: '8px',
                        textAlign: 'left',
                        boxShadow:
                          secondaryTabId === `documents-${member?.id}`
                            ? '0 1px 3px rgba(7, 93, 197, 0.12), inset 0 1px 0 rgba(255,255,255,0.6)'
                            : '0 1px 2px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          backgroundColor:
                            secondaryTabId === `documents-${member?.id}` ? '#dbeafe' : '#f1f5f9',
                          color:
                            secondaryTabId === `documents-${member?.id}` ? '#075dc5' : '#64748b',
                        }}
                      >
                        <FileStack size={15} strokeWidth={2.25} />
                      </div>
                      Documents
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setActiveSecondaryTabs(prev => ({
                          ...prev,
                          [member?.id]: `tracking-${member?.id}`,
                        }))
                      }
                      className="sidebar-menu-btn"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border:
                          secondaryTabId === `tracking-${member?.id}`
                            ? '1px solid #bcd7ff'
                            : '1px solid #e2e8f0',
                        background:
                          secondaryTabId === `tracking-${member?.id}` ? '#eff6ff' : '#ffffff',
                        color: secondaryTabId === `tracking-${member?.id}` ? '#075dc5' : '#334155',
                        fontWeight: secondaryTabId === `tracking-${member?.id}` ? 600 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        borderRadius: '8px',
                        textAlign: 'left',
                        boxShadow:
                          secondaryTabId === `tracking-${member?.id}`
                            ? '0 1px 3px rgba(7, 93, 197, 0.12), inset 0 1px 0 rgba(255,255,255,0.6)'
                            : '0 1px 2px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          backgroundColor:
                            secondaryTabId === `tracking-${member?.id}` ? '#dbeafe' : '#f1f5f9',
                          color:
                            secondaryTabId === `tracking-${member?.id}` ? '#075dc5' : '#64748b',
                        }}
                      >
                        <LineChart size={15} strokeWidth={2.25} />
                      </div>
                      Tracking Details
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setActiveSecondaryTabs(prev => ({
                          ...prev,
                          [member?.id]: `processed-docs-${member?.id}`,
                        }))
                      }
                      className="sidebar-menu-btn"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border:
                          secondaryTabId === `processed-docs-${member?.id}`
                            ? '1px solid #bcd7ff'
                            : '1px solid #e2e8f0',
                        background:
                          secondaryTabId === `processed-docs-${member?.id}` ? '#eff6ff' : '#ffffff',
                        color:
                          secondaryTabId === `processed-docs-${member?.id}` ? '#075dc5' : '#334155',
                        fontWeight: secondaryTabId === `processed-docs-${member?.id}` ? 600 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '10px',
                        borderRadius: '8px',
                        textAlign: 'left',
                        boxShadow:
                          secondaryTabId === `processed-docs-${member?.id}`
                            ? '0 1px 3px rgba(7, 93, 197, 0.12), inset 0 1px 0 rgba(255,255,255,0.6)'
                            : '0 1px 2px rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          backgroundColor:
                            secondaryTabId === `processed-docs-${member?.id}`
                              ? '#dbeafe'
                              : '#f1f5f9',
                          color:
                            secondaryTabId === `processed-docs-${member?.id}`
                              ? '#075dc5'
                              : '#64748b',
                        }}
                      >
                        <ScanLine size={15} strokeWidth={2.25} />
                      </div>
                      Forms by Documitra
                    </button>
                  </div>
                </div>

                {/* ======================================== */}
                {/* SECONDARY CONTENT PANEL â€” per member */}
                {/* ======================================== */}
                <div style={{ paddingTop: '0', flex: 1, minWidth: 0 }}>
                  {/* Application Status (moved from Documents bottom) + Member Name on the right */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      flexShrink: 0,
                    }}
                  >
                    {/* Application Status Label + Select + Update */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px 16px',
                        marginBottom: '15px',
                        borderRadius: '8px',
                        border: '1px solid #86efac',
                        overflow: 'hidden',
                        backgroundColor: '#f0fdf4',
                        color: '#15803d',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
                      }}
                    >
                      <Text style={{ fontSize: '16px', fontWeight: '600', minWidth: '80px' }}>
                        Application Status:
                      </Text>
                      <Select
                        style={{ width: 300, fontSize: '16px' }}
                        showSearch
                        optionFilterProp="label"
                        optionLabelProp="displayLabel"
                        filterOption={(input, option) => {
                          const opt = option as unknown as Record<string, any>;
                          const label = String(opt?.searchLabel ?? opt?.name ?? opt?.value ?? '');
                          return label.toLowerCase().includes(input.toLowerCase());
                        }}
                        value={memberStatusMap[member?.id] ?? ''}
                        onChange={value =>
                          setMemberStatusMap(prev => ({ ...prev, [member?.id]: value }))
                        }
                        loading={!!memberStatusUpdating[member?.id]}
                        placeholder="Select member status"
                        menuItemSelectedIcon={null}
                        options={(() => {
                          const currentSelectedValue = memberStatusMap[member?.id] ?? '';
                          return partnerMemberStatusOptions.map((opt: any) => {
                            const optionValue = String(opt?.value ?? opt?.name ?? '');
                            const searchLabel = String(
                              opt?.label ?? opt?.name ?? optionValue ?? ''
                            );
                            const isSelected = optionValue === String(currentSelectedValue);
                            return {
                              ...opt,
                              searchLabel,
                              displayLabel: searchLabel,
                              label: (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '2px 0',
                                    fontSize: '14px',
                                    fontWeight: isSelected ? 600 : 500,
                                    color: isSelected ? '#075dc5' : '#1f2937',
                                    lineHeight: 1.2,
                                    width: '100%',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      borderRadius: '3px',
                                      border: isSelected
                                        ? '2px solid #075dc5'
                                        : '1.5px solid #94a3b8',
                                      backgroundColor: isSelected ? '#075dc5' : '#ffffff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      transition:
                                        'background-color 0.15s ease, border-color 0.15s ease',
                                      boxShadow: isSelected
                                        ? '0 1px 3px rgba(7, 93, 197, 0.3)'
                                        : 'none',
                                    }}
                                  >
                                    {isSelected && (
                                      <Check
                                        size={11}
                                        strokeWidth={3.5}
                                        color="#ffffff"
                                        style={{ display: 'block' }}
                                      />
                                    )}
                                  </div>
                                  <span
                                    style={{
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {searchLabel}
                                  </span>
                                </div>
                              ),
                            };
                          });
                        })()}
                        size="middle"
                      />
                      <Button
                        type="primary"
                        onClick={() => handleMemberStatusUpdateFromHeader(member)}
                        loading={!!memberStatusUpdating[member?.id]}
                        disabled={!memberStatusMap[member?.id]}
                        style={{
                          fontSize: '16px',
                          height: '32px',
                          padding: '0 20px',
                          backgroundColor: memberStatusMap[member?.id] ? '#004195' : undefined,
                          borderColor: memberStatusMap[member?.id] ? '#004195' : undefined,
                          borderRadius: '4px',
                        }}
                      >
                        Update
                      </Button>
                    </div>
                  </div>
                  {/* ------ Form Details secondary tab ------ */}
                  {secondaryTabId === `form-details-${member?.id}` && (
                    <ReviewPartnerOrderMember
                      formInput={props.formInput}
                      index={members.indexOf(member)}
                      form={props.form}
                      formDetails={{ ...member, group: formDetails }}
                      showOnlyForm={true}
                    />
                  )}

                  {/* ------ Documents secondary tab ------ */}
                  {secondaryTabId === `documents-${member?.id}` && (
                    <div style={orderCardStyle}>
                      <div style={sectionTitleStyle}>Review Documents</div>
                      <div style={{ marginTop: '8px' }}>
                        <ReviewPartnerOrderMember
                          formInput={props.formInput}
                          index={members.indexOf(member)}
                          form={props.form}
                          formDetails={{ ...member, group: formDetails }}
                          showOnlyDocuments={true}
                        />
                      </div>
                    </div>
                  )}

                  {/* ------ Forms by Documitra (Processed Documents) secondary tab ------ */}
                  {secondaryTabId === `processed-docs-${member?.id}` && (
                    <div style={orderCardStyle}>
                      <div style={sectionTitleStyle}>Processed Documents</div>
                      <div style={{ marginTop: '8px' }}>
                        <ReviewPartnerOrderMember
                          formInput={props.formInput}
                          index={members.indexOf(member)}
                          form={props.form}
                          formDetails={{ ...member, group: formDetails }}
                          showOnlyProcessed={true}
                          hideApplicationStatus={true}
                          onMemberStatusUpdated={handleMemberStatusUpdated}
                        />
                      </div>
                    </div>
                  )}

                  {/* ------ Tracking Details secondary tab (UNDER EACH MEMBER) ------ */}
                  {secondaryTabId === `tracking-${member?.id}` &&
                    renderMemberTrackingDetails(member)}
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default ReviewPartnerOrder;
